import React, { useState, useRef, useEffect } from "react";
import { useCoop } from "../../contexts/CoopContext";
import { useAuth } from "../../contexts/AuthContext";
import { useGame } from "../../contexts/GameContext";
import { MessageCircle, Send, X, Users } from "lucide-react";

/**
 * Floating co-op chat / minimap widget.
 * Renders nothing when not in a coop lobby.
 * Toggleable button bottom-right; expanded panel shows members + chat.
 */
export const CoopChatWidget = () => {
  const { user } = useAuth();
  const { lobby, coopMode, chatMessages, sendChat, wsStatus } = useCoop();
  const { bumpMeta } = useGame();
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const [unread, setUnread] = useState(0);
  const lastSeenIndex = useRef(0);
  const messagesRef = useRef(null);

  useEffect(() => {
    if (open) {
      lastSeenIndex.current = chatMessages.length;
      setUnread(0);
      // scroll bottom
      requestAnimationFrame(() => {
        if (messagesRef.current) messagesRef.current.scrollTop = messagesRef.current.scrollHeight;
      });
    } else {
      const delta = chatMessages.length - lastSeenIndex.current;
      if (delta > 0) setUnread(delta);
    }
  }, [chatMessages, open]);

  if (!coopMode || !lobby) return null;

  const handleSend = (e) => {
    e?.preventDefault?.();
    const text = input.trim();
    if (!text) return;
    sendChat(text);
    setInput("");
    // Succès "Roi des bavards" : 50 messages envoyés dans le chat coop
    bumpMeta?.("coopChatSent", 1, { 50: "yapper" });
  };

  return (
    <>
      {!open && (
        <button
          data-testid="coop-chat-toggle"
          onClick={() => setOpen(true)}
          className="fixed bottom-4 right-4 z-50 bg-fuchsia-500 text-white border-4 border-slate-950 rounded-full w-14 h-14 flex items-center justify-center cartoon-shadow hover:-translate-y-0.5 transition-transform"
          title={`Co-op (${lobby.members.length}/4)`}
        >
          <MessageCircle className="w-6 h-6" />
          {unread > 0 && (
            <span className="absolute -top-1 -right-1 bg-amber-400 text-slate-950 border-2 border-slate-950 rounded-full px-1.5 text-xs font-bold">
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </button>
      )}

      {open && (
        <div
          data-testid="coop-chat-panel"
          className="fixed bottom-4 right-4 z-50 w-80 max-w-[calc(100vw-2rem)] bg-slate-800 border-4 border-slate-950 rounded-2xl cartoon-shadow-lg-static flex flex-col overflow-hidden"
          style={{ maxHeight: "70vh" }}
        >
          <div className="flex items-center justify-between bg-fuchsia-500 border-b-4 border-slate-950 px-3 py-2">
            <div className="flex items-center gap-2 text-white font-display font-bold text-sm">
              <Users className="w-4 h-4" />
              Co-op · {lobby.code}
              <span className={`text-xs ${wsStatus === "connected" ? "text-emerald-200" : "text-amber-200"}`}>
                {wsStatus === "connected" ? "●" : "○"}
              </span>
            </div>
            <button
              data-testid="coop-chat-close"
              onClick={() => setOpen(false)}
              className="bg-slate-900 text-white border-2 border-slate-950 rounded-md w-6 h-6 flex items-center justify-center"
            >
              <X className="w-3 h-3" />
            </button>
          </div>

          {/* Members strip */}
          <div className="bg-slate-900 border-b-2 border-slate-950 px-2 py-2">
            <div className="flex gap-1.5 flex-wrap">
              {lobby.members.map((m) => (
                <div
                  key={m.user_id}
                  data-testid={`coop-member-${m.user_id}`}
                  className={`flex items-center gap-1 ${m.connected ? "bg-emerald-500/20 border-emerald-400" : "bg-slate-700/40 border-slate-600"} border-2 rounded-lg px-1.5 py-0.5 text-[10px] font-bold`}
                  title={m.connected ? "En ligne" : "Hors-ligne"}
                >
                  {m.picture && <img src={m.picture} alt="" className="w-4 h-4 rounded-full" />}
                  <span className="text-slate-100 max-w-[60px] truncate">{m.name}{m.user_id === user?.user_id && " (toi)"}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Messages */}
          <div
            ref={messagesRef}
            data-testid="coop-chat-messages"
            className="flex-1 overflow-y-auto px-2 py-2 space-y-1 bg-slate-900 text-sm min-h-[120px]"
          >
            {chatMessages.length === 0 ? (
              <p className="text-slate-500 text-xs italic text-center py-4">
                Personne n'a encore parlé. Dis bonjour à ton équipe !
              </p>
            ) : (
              chatMessages.map((m, i) => {
                const isMe = m.from_user === user?.user_id;
                return (
                  <div key={i} className={`flex ${isMe ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[85%] rounded-xl px-2 py-1 ${isMe ? "bg-sky-500 text-white" : "bg-slate-700 text-slate-100"} border-2 border-slate-950`}>
                      <p className={`text-[10px] font-bold ${isMe ? "text-sky-100" : "text-fuchsia-300"} mb-0.5`}>{m.from_name}{isMe && " (toi)"}</p>
                      <p className="text-xs break-words">{m.text}</p>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* Input */}
          <form onSubmit={handleSend} className="flex gap-1 p-2 border-t-2 border-slate-950 bg-slate-800">
            <input
              data-testid="coop-chat-input"
              type="text"
              maxLength={240}
              placeholder="Message…"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              className="flex-1 bg-slate-900 text-white border-2 border-slate-950 rounded-lg px-2 py-1 text-xs"
            />
            <button
              data-testid="coop-chat-send"
              type="submit"
              disabled={!input.trim()}
              className="bg-sky-400 text-slate-950 border-2 border-slate-950 rounded-lg px-2 py-1 disabled:opacity-50"
            >
              <Send className="w-3.5 h-3.5" />
            </button>
          </form>
        </div>
      )}
    </>
  );
};
